import { useState } from 'react';
import { EditableSection } from '../helpers/EditableSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { QuestionModal } from './QuestionModal';
import { ConfirmationModal } from '../helpers/ConfirmationModal';
import { QuestionDetailsView } from './QuestionDetailsView';
import { FiPlus, FiChevronDown, FiChevronUp, FiEdit } from 'react-icons/fi';
import { FaRegTrashCan } from 'react-icons/fa6';
import { Question } from '@mightybyte/business';
import { trpc } from '../../lib/trpc';
import { toast } from 'sonner';
import { CustomButton } from '@/components/uiWrappers/button.tsx';

interface QuizQuestionsProps {
  quizId: string;
  quizNumber: number;
  questions: Question[];
}

export function QuizQuestions({ quizId, quizNumber, questions }: QuizQuestionsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; question: Question | null }>({
    isOpen: false,
    question: null,
  });

  const utils = trpc.useUtils();

  const toggleQuestion = (questionNumber: number) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionNumber)) {
        newSet.delete(questionNumber);
      } else {
        newSet.add(questionNumber);
      }
      return newSet;
    });
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingQuestion(null);
  };

  const deleteQuestionMutation = trpc.admin.quiz.deleteQuestion.useMutation({
    onSuccess: async () => {
      await utils.admin.quiz.getById.invalidate({ id: quizId });
      toast.success('Question deleted successfully!');
      setDeleteConfirmModal({ isOpen: false, question: null });
    },
    onError: (error) => {
      toast.error(`Failed to delete question: ${error.message}`);
    },
  });

  const handleDeleteQuestion = (question: Question) => {
    setDeleteConfirmModal({ isOpen: true, question });
  };

  const confirmDelete = () => {
    if (deleteConfirmModal.question) {
      deleteQuestionMutation.mutate({
        quizId,
        questionId: deleteConfirmModal.question.id,
      });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmModal({ isOpen: false, question: null });
  };

  return (
    <>
      <EditableSection
        title={`Questions (${questions.length})`}
        collapsible={true}
        defaultExpanded={false}
        customAction={
          <Button
            variant="outline"
            onClick={() => setIsModalOpen(true)}
            className="gap-1"
          >
            <FiPlus />
            Add question
          </Button>
        }
      >
        {questions.length === 0 ? (
          <div className="text-foreground/60 text-sm">
            No questions added yet
          </div>
        ) : (
          <div className="space-y-3">
            {questions.map((question) => {
              const isExpanded = expandedQuestions.has(question.number);

              return (
                <Card key={question.number} className="p-4 shadow-xs bg-muted/80">
                  {/* Question Header */}
                  <div className="flex items-start gap-3">
                    {/* Expand/Collapse Icon on Left */}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => toggleQuestion(question.number)}
                      className="mt-1"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? (
                        <FiChevronUp className="w-5 h-5" />
                      ) : (
                        <FiChevronDown className="w-5 h-5" />
                      )}
                    </Button>

                    {/* Question Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className=" text-base text-foreground/80">
                        Question {question.number}
                      </h3>
                      <p className="text-base font-semibold mt-1">
                        {question.question}
                      </p>
                    </div>

                    {/* Right Actions */}
                    <div className="flex items-center gap-1">
                      <CustomButton variant={'secondary'}
                                    onClick={() => handleEditQuestion(question)}
                      >
                        <FiEdit className="w-5 h-5" />
                        Edit
                      </CustomButton>
                      <button
                        onClick={() => handleDeleteQuestion(question)}
                        className="w-[38px] h-[38px] text-red-500 bg-red-50 rounded-lg transition-colors justify-center items-center flex cursor-pointer"
                        title="Delete question"
                      >
                        <FaRegTrashCan className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <CardContent className=" border-t px-0 bg-muted/80">
                      <QuestionDetailsView question={question} />
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </EditableSection>

      <QuestionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        quizId={quizId}
        quizNumber={quizNumber}
        editingQuestion={editingQuestion}
      />

      <ConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Question"
        description={`Are you sure you want to delete Question ${deleteConfirmModal.question?.number}? This action cannot be undone.`}
        confirmText="Delete Question"
        cancelText="Cancel"
        isLoading={deleteQuestionMutation.isPending}
        highlightBox={{
          text: `"${deleteConfirmModal.question?.question}"`,
          bgColor: 'bg-muted',
          textColor: 'text-foreground',
        }}
      />
    </>
  );
}
